import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import usersRequest from "../../request/usersRequest";
import { CombinedContext } from "../../context/combinedContext";
import PhoneInput from "../common/phoneInput/phoneInput";
import imageCompression from "browser-image-compression";
import translations from "../../translations/translations";
import companyBasedRequest from "../../request/companyBasedRequest";
import TermsOfServiceModal from "../common/termsOfServiceModal/termsOfServiceModal";
import PasswordField from "../common/passwordField/passwordField";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import RegisterStaffSkeleton from "./registerStaffSkeleton/registerStaffSkeleton";

const RegisterStaff = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const navigate = useNavigate();

    const [rememberMe, setRememberMe] = useState(false);

    const [inviteValid, setInviteValid] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [banner, setBanner] = useState(null);
    const [exiting, setExiting] = useState(false);
    const [adminId, setAdminId] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);

    // Form states
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [category, setCategory] = useState("");
    const [shopId, setShopId] = useState("");
    const [inviteId, setInviteId] = useState("");
    const [activeModal, setActiveModal] = useState(null);
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const fileInputRef = useRef(null);
    const maxSlots = 5;

    const { userData, setUserData, language } = useContext(CombinedContext);

    const showBanner = (type, text, duration = 2500) => {
        setBanner({ type, text });
        setExiting(false);

        setTimeout(() => setExiting(true), duration - 250);
        setTimeout(() => {
            setBanner(null);
            setExiting(false);
        }, duration);
    };

    useEffect(() => {
        // if (userData?.user?.role === "admin") return;

        const verifyInvite = async () => {
            try {
                const res = await companyBasedRequest.verifyInvite(token);

                setInviteId(res.inviteId);
                setShopId(res.shopId);
                setFirstName(res.firstName);
                setLastName(res.lastName);
                setEmail(res.email || "");
                setInviteValid(true);

                if (res.adminVerified && !res.used) {
                    if (!userData || !userData.cognitoTokens?.IdToken) {
                        navigate("/signin");
                        return;
                    }
                    setSuccess("Welcome back! Continue registering your shop.");
                }

                // ✅ Mark as verified
                sessionStorage.setItem("inviteVerified", "true");
            } catch (err) {
                showBanner(
                    "error",
                    language === "es" ? "Enlace de invitación inválido o expirado" : "Invalid or expired invite link"
                );
            } finally {
                setVerifying(false);
            }
        };

        verifyInvite();
    }, [token]);

    useEffect(() => {
        if (userData && userData.user.role === "owner" && userData.user.onboardingStatus === "pending-shop") {
            setAdminId(userData.userId);
        }
    }, [userData]);

    if (verifying) {
        return <RegisterStaffSkeleton />;
    }

    const normalizePhoneNumber = (raw) => {
        if (!raw) return "";
        let cleaned = raw.trim();

        cleaned = cleaned.replace(/[^0-9+]/g, "");

        if (cleaned.startsWith("++")) cleaned = cleaned.replace(/^\++/, "+");

        if (!cleaned.startsWith("+")) cleaned = "+1" + cleaned;

        cleaned = cleaned.replace(/(?!^)\+/g, "");

        if (!/^\+\d{10,15}$/.test(cleaned)) {
            console.warn("⚠️ Invalid phone format:", cleaned);
            return "";
        }

        return cleaned;
    };

    function isStrongPassword(password) {
        return (
            typeof password === "string" &&
            password.length >= 8 &&
            /[A-Z]/.test(password) &&
            /[a-z]/.test(password) &&
            /[0-9]/.test(password) &&
            /[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`+=-]/.test(password)
        );
    }

    const handleRegisterAdmin = async () => {
        if (!firstName || !lastName || !phoneNumber || !password || !confirmPassword) {
            showBanner(
                "error",
                language === "es"
                    ? "Por favor completa todos los campos obligatorios"
                    : "Please fill out all required fields"
            );
            return;
        }

        if (!acceptedTerms) {
            showBanner("error", translations[language].registerStaff.errors.mustAcceptTerms);
            return;
        }

        if (!password || !confirmPassword) {
            showBanner(
                "error",
                language === "es"
                    ? "La contraseña y su confirmación son obligatorias"
                    : "Password and confirm password are required"
            );
            return;
        }

        // 2️⃣ Strong password
        if (!isStrongPassword(password)) {
            showBanner(
                "error",
                language === "es"
                    ? "La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número y un carácter especial"
                    : "Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a special character"
            );
            return;
        }

        // 3️⃣ Match check
        if (password !== confirmPassword) {
            showBanner("error", language === "es" ? "Las contraseñas no coinciden" : "Passwords do not match");
            return;
        }

        setLoading(true);

        try {
            const payload = {
                token,
                shopId,
                inviteId,
                email,
                password,
                firstName,
                lastName,
                phone: normalizePhoneNumber(phoneNumber),
                acceptedTermsVersion: "1.0",
            };

            const res = await companyBasedRequest.registerStaff(payload);

            // 🧠 Normalize structure to match login and context
            const formattedData = {
                ...res,
                cognitoTokens: {
                    IdToken: res.cognitoTokens?.IdToken || res.IdToken,
                    AccessToken: res.cognitoTokens?.AccessToken || res.AccessToken,
                    RefreshToken: res.cognitoTokens?.RefreshToken || res.RefreshToken,
                },
            };

            setUserData(formattedData);
            setAdminId(res.adminId);
            navigate("/calendar");

            // 💾 Store tokens & user persistently
            const storage = rememberMe ? localStorage : sessionStorage;
            if (formattedData.cognitoTokens?.IdToken) storage.setItem("idToken", formattedData.cognitoTokens.IdToken);
            if (formattedData.cognitoTokens?.AccessToken)
                storage.setItem("accessToken", formattedData.cognitoTokens.AccessToken);
            if (formattedData.cognitoTokens?.RefreshToken)
                storage.setItem("refreshToken", formattedData.cognitoTokens.RefreshToken);
            storage.setItem("user", JSON.stringify(formattedData.user));
        } catch (err) {
            console.error("Register admin failed:", err);
            showBanner(
                "error",
                err.message || (language === "es" ? "Error al registrar usuario" : "Failed to register staff")
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container">
            <div className="card" style={{ maxWidth: 400, margin: "40px auto" }}>
                <div className="hd">
                    <div>{translations[language].registerStaff.step1.title}</div>
                </div>

                <div className="bd">
                    {success && (
                        <div className="sub" style={{ color: "green", marginBottom: 10 }}>
                            {success}
                        </div>
                    )}

                    <>
                        <input className="input" type="email" placeholder={translations[language].registerStaff.step1.email} value={email} disabled style={{ marginBottom: 10 }} />
                        <div className="row" style={{ marginBottom: 10 }}>
                            <input
                                className="input"
                                placeholder={translations[language].registerStaff.step1.firstName}
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                style={{ marginRight: 5 }}
                            />
                            <input
                                className="input"
                                placeholder={translations[language].registerStaff.step1.lastName}
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                            />

                            <PhoneInput businessPhone={phoneNumber} setBusinessPhone={setPhoneNumber} />
                            <PasswordField
                                value={password}
                                onChange={setPassword}
                                placeholder={translations[language].registerStaff.step1.password}
                                autoComplete="new-password"
                            />

                            <PasswordField
                                value={confirmPassword}
                                onChange={setConfirmPassword}
                                placeholder={translations[language].registerStaff.step1.confirmPassword}
                                autoComplete="new-password"
                            />
                        </div>

                        <div style={{ marginBottom: 10, display: "flex", alignItems: "center" }}>
                            <input
                                type="checkbox"
                                id="rememberMe"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                style={{ marginRight: 6 }}
                            />
                            <label htmlFor="rememberMe">{translations[language].registerStaff.step1.remember}</label>
                        </div>

                        <div className="terms-checkbox">
                            <label className="terms-label">
                                <input
                                    type="checkbox"
                                    checked={acceptedTerms}
                                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                                    style={{ marginRight: 6 }}
                                />
                                <span>
                                    {translations[language].register.acceptTerms}{" "}
                                    <span className="sf-terms-span" onClick={() => setActiveModal("terms")}>
                                        {translations[language].register.termsLink}
                                    </span>
                                </span>
                            </label>
                        </div>

                        {banner && <BannerMessageFixed type={banner.type} text={banner.text} exiting={exiting} />}

                        <button
                            className="btn brand"
                            style={{ width: "100%", marginBottom: 10 }}
                            onClick={handleRegisterAdmin}
                            disabled={loading}
                        >
                            {loading
                                ? translations[language].registerStaff.step1.registering
                                : translations[language].registerStaff.step1.register}
                        </button>
                    </>
                </div>
            </div>
            {activeModal === "terms" && <TermsOfServiceModal isOpen onClose={() => setActiveModal(null)} />}
        </div>
    );
};

export default RegisterStaff;
